import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { IDropdownSettings } from 'ng-multiselect-dropdown'
import { ToastrService } from 'ngx-toastr'
import { NgxUiLoaderService } from 'ngx-ui-loader'
import { throwError } from 'rxjs'
import { catchError, finalize, first, map, switchMap } from 'rxjs/operators'
import { FuelCategory } from 'src/app/core/enums'
import { Flag } from 'src/app/core/models/flag'
import { FuelEUType } from 'src/app/core/models/fueleu'
import { VoyageDataBioFuelPayload } from 'src/app/core/models/voyage-data-management'
import { FueleuTypesService } from 'src/app/core/services/fueleu-types.service'

@Component({
  selector: 'fuelseu-details',
  templateUrl: './fuelseu-details.component.html',
  styleUrls: ['./fuelseu-details.component.css'],
})
export class FuelsEUDetailsComponent implements OnInit {
  @Input('fueltype') FuelseuId: string
  @Input() modal: NgbActiveModal
  @Input() fuelEuDetails: VoyageDataBioFuelPayload

  @Output() onDetails = new EventEmitter()

  formGroup!: FormGroup
  dropdownSettings: IDropdownSettings

  constructor(
    private ngxService: NgxUiLoaderService,
    private fuelseuTypesService: FueleuTypesService,
    private toastr: ToastrService,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.formGroup = new FormGroup({
      fuelName: new FormControl(this.fuelEuDetails?.fuelName || '', [
        Validators.required,
      ]),
      fuelCategory: new FormControl(-1, [Validators.required]),
      lowerCalorificValue: new FormControl(
        this.fuelEuDetails?.lowerCalorificValue || 0,
        [Validators.required]
      ),
      cO2eqWtT: new FormControl(0, [Validators.required]),
      cO2eqWtW: new FormControl(0, [Validators.required]),
      cO2eqTtWInclSlip: new FormControl(0, [Validators.required]),
      cO2ConversionFactor: new FormControl(0, [Validators.required]),
      cH4ConversionFactor: new FormControl(0, [Validators.required]),
      n2OConversionFactor: new FormControl(0, [Validators.required]),
      e: new FormControl(this.fuelEuDetails?.e || 0, [Validators.required]),
    })

    this.dropdownSettings = {
      singleSelection: false,
      idField: 'id',
      textField: 'name',
      itemsShowLimit: 5,
      allowSearchFilter: true,
      defaultOpen: false,
    }

    if (this.FuelseuId) this.getFuelseuDetails()
  }

  getFuelseuDetails() {
    this.ngxService.startLoader('loader-01')
    this.fuelseuTypesService.getFuelEUType(this.FuelseuId).subscribe(
      res => {
        const data = res['data']
        const editFormValue = {
          fuelName: data.fuelName,
          fuelCategory: data.fuelCategory,
          lowerCalorificValue: data.lowerCalorificValue,
          cO2eqWtT: data.cO2eqWtT,
          cO2eqWtW: data.cO2eqWtW,
          cO2eqTtWInclSlip: data.cO2eqTtWInclSlip,
          cO2ConversionFactor: data.cO2ConversionFactor,
          cH4ConversionFactor: data.cH4ConversionFactor,
          n2OConversionFactor: data.n2OConversionFactor,
          e: data.e,
        }
        this.formGroup.patchValue(editFormValue)
        this.ngxService.stopLoader('loader-01')
      },
      error => {
        this.ngxService.stopLoader('loader-01')
        console.error('Error fetching fuel type details:', error)
      }
    )
  }

  onSave() {
    const formValues = this.formGroup.value
    if (this.formGroup.invalid || formValues.fuelCategory == -1) {
      this.toastr.error('Please fill in all fields', 'Form validation error')
      return
    }
    this.ngxService.startLoader('loader-01')
    const Fuelseu: FuelEUType = {
      fuelName: formValues.fuelName || '',
      fuelCategory:
        formValues.fuelCategory !== undefined
          ? Number(formValues.fuelCategory)
          : 0,
      lowerCalorificValue:
        formValues.lowerCalorificValue !== undefined
          ? Number(formValues.lowerCalorificValue)
          : 0,
      cO2eqWtT:
        formValues.cO2eqWtT !== undefined ? Number(formValues.cO2eqWtT) : 0,
      cO2eqWtW:
        formValues.cO2eqWtW !== undefined ? Number(formValues.cO2eqWtW) : 0,
      cO2eqTtWInclSlip:
        formValues.cO2eqTtWInclSlip !== undefined
          ? Number(formValues.cO2eqTtWInclSlip)
          : 0,
      cO2ConversionFactor:
        formValues.cO2ConversionFactor !== undefined
          ? Number(formValues.cO2ConversionFactor)
          : 0,
      cH4ConversionFactor:
        formValues.cH4ConversionFactor !== undefined
          ? Number(formValues.cH4ConversionFactor)
          : 0,
      n2OConversionFactor:
        formValues.n2OConversionFactor !== undefined
          ? Number(formValues.n2OConversionFactor)
          : 0,
      e: formValues.e !== undefined ? Number(formValues.e) : 0,
    }

    if (this.FuelseuId) {
      // Update existing FuelEUType
      this.fuelseuTypesService
        .updateFuelEUType(this.FuelseuId, Fuelseu)
        .pipe(
          first(),
          catchError(error => {
            this.toastr.error("Couldn't update FuelType")
            return throwError(() => new Error(error))
          }),
          finalize(() => this.ngxService.stopLoader('loader-01'))
        )
        .subscribe(() => {
          this.toastr.success('FuelType updated successfully')
          this.onDetails.emit(this.formGroup.value)
          this.modal.close({ action: 'Save', data: this.formGroup.value })
        })
    } else {
      // Create new FuelEUType
      this.fuelseuTypesService
        .createFuelEUType(Fuelseu)
        .pipe(
          first(),
          catchError(error => {
            this.toastr.error("Couldn't create FuelType")
            return throwError(() => new Error(error))
          }),
          finalize(() => this.ngxService.stopLoader('loader-01'))
        )
        .subscribe(() => {
          this.toastr.success('FuelType created successfully')
          this.onDetails.emit(this.formGroup.value)
          this.modal.close({ action: 'Save', data: this.formGroup.value })
        })
    }
  }
}
